import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom, invokeFunction } from '@/integrations/pms';
import { toast } from 'sonner';

interface UseAutoHeroImageOptions {
  entityType: 'location' | 'region' | 'service' | 'blog';
  entityId: string | undefined;
  entityName: string | undefined;
  currentImage: string | null | undefined;
  industry?: string;
  state?: string;
}

/**
 * Auto-generates a hero image via AI when:
 * 1. The entity has no featured_image
 * 2. page_design_config has ai_hero_auto_generate = true for this page type
 *
 * Also exposes `generate()` for manual triggering by admins.
 */
export const useAutoHeroImage = ({
  entityType,
  entityId,
  entityName,
  currentImage,
  industry,
  state,
}: UseAutoHeroImageOptions) => {
  const queryClient = useQueryClient();
  const triggeredRef = useRef<string | null>(null);

  // Fetch design config for this page type
  const { data: designConfig } = useQuery({
    queryKey: ['page-design-config', entityType],
    queryFn: async () => {
      const { data } = await pmsFrom('page_design_config')
        .select('ai_hero_auto_generate, ai_hero_image_style')
        .eq('page_type', entityType)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await invokeFunction('generate-hero-image', {
        body: {
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName,
          image_style: designConfig?.ai_hero_image_style || 'city_landmarks',
          industry: industry || '',
          state: state || '',
        },
      });
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      toast.success('Hero image generated successfully!');
      // Invalidate the entity query so the new image shows up
      const queryKeyMap: Record<string, string> = {
        location: 'service-location',
        region: 'service-region',
        service: 'service-detail',
        blog: 'blog-post',
      };
      queryClient.invalidateQueries({ queryKey: [queryKeyMap[entityType]] });
    },
    onError: (err: Error) => {
      if (err.message?.includes('credits') || err.message?.includes('402')) {
        toast.error('Insufficient AI credits. Please add more credits.');
      } else if (err.message?.includes('429') || err.message?.includes('Rate limit')) {
        toast.error('Rate limit exceeded. Please try again later.');
      } else {
        toast.error(err.message || 'Failed to generate hero image');
      }
    },
  });

  // Auto-generate on first load if no image and setting enabled
  useEffect(() => {
    if (
      !entityId ||
      !entityName ||
      currentImage ||
      !designConfig?.ai_hero_auto_generate ||
      triggeredRef.current === entityId ||
      generateMutation.isPending
    ) {
      return;
    }

    triggeredRef.current = entityId;
    generateMutation.mutate();
  }, [entityId, entityName, currentImage, designConfig?.ai_hero_auto_generate]);

  return {
    isGenerating: generateMutation.isPending,
    generatedUrl: generateMutation.data?.image_url,
    error: generateMutation.error,
    generate: () => {
      if (entityId && entityName) {
        generateMutation.mutate();
      }
    },
  };
};
